import { useMemo } from 'react'

import type {
  ActivityRow,
  CashSnapshot,
  Holding,
  HoldingRow,
  PortfolioAccount,
  PortfolioAccountSummary,
  PortfolioDashboard,
  PortfolioPeriodOption,
  RealizedRow,
  RowContext,
  UnvestedAwardRow,
  YearToDateRow,
} from './types'
import { useProjectSecurities } from './useProjectSecurities'

// One missing value makes the whole total a guess, so the total is unknown rather than short. With
// nothing to add up there is no total either, which keeps an activity-only period off $0.00.
export const sumOrNull = (values: (number | null)[]): number | null =>
  values.length === 0 || values.some((value) => value == null)
    ? null
    : values.reduce<number>((total, value) => total + (value ?? 0), 0)

type HoldingTotals = Pick<
  Holding,
  'marketValue' | 'costBasis' | 'unrealizedGainLoss'
>

const CASH_LIKE_CLASSES = new Set([
  'cash',
  'cash_sweep',
  'cash_equivalent',
  'cash_equivalents',
  'money_market',
])

export const isCashLike = (assetClass: string): boolean =>
  CASH_LIKE_CLASSES.has(assetClass.trim().toLowerCase())

// A printed line with no shares and no value is a closed position, not something held.
export const isPosition = (
  holding: Pick<Holding, 'quantity' | 'marketValue'>,
): boolean => (holding.quantity ?? 0) !== 0 || (holding.marketValue ?? 0) !== 0

// The cash the holdings do not already carry. A money-market core fund is both a holding and the
// cash chain's balance, so only cash beyond the cash-like holdings is new money. A negative balance
// is a margin debit: it is owed on top of the holdings, so it lowers the value as printed.
export const cashBeyondHoldings = (
  cashBalance: number,
  holdings: Pick<Holding, 'assetClass' | 'marketValue'>[],
): number => {
  if (cashBalance < 0) return cashBalance
  const cashLike = holdings
    .filter((holding) => isCashLike(holding.assetClass))
    .reduce((total, holding) => total + (holding.marketValue ?? 0), 0)
  return Math.max(cashBalance - cashLike, 0)
}

// What the account is worth: securities plus the cash the statement reconciled. Unknown when the
// cash side printed no balance, so the number is never short without saying so.
export const accountValue = (
  marketValue: number,
  cashBalance: number | null,
  holdings: Pick<Holding, 'assetClass' | 'marketValue'>[],
): number | null =>
  cashBalance == null
    ? null
    : marketValue + cashBeyondHoldings(cashBalance, holdings)

// The money figures for one snapshot of holdings. The engine's own per-row unrealized gain wins
// over market value minus cost, since a statement can print the gain without printing a cost basis.
export const snapshotTotals = (holdings: HoldingTotals[]) => {
  const marketValue = holdings.reduce(
    (total, holding) => total + (holding.marketValue ?? 0),
    0,
  )
  const costBasis = sumOrNull(holdings.map((holding) => holding.costBasis))
  const unrealized =
    sumOrNull(holdings.map((holding) => holding.unrealizedGainLoss)) ??
    (costBasis == null ? null : marketValue - costBasis)

  return {
    marketValue,
    costBasis,
    unrealized,
    unrealizedPercent:
      costBasis != null && costBasis > 0 && unrealized != null
        ? (unrealized / costBasis) * 100
        : null,
  }
}

const summarize = (account: PortfolioAccount): PortfolioAccountSummary => {
  // Periods arrive oldest -> newest from the fold. The newest one can carry only activity, so the
  // current snapshot is the newest period that actually lists positions.
  const latest =
    [...account.periods]
      .reverse()
      .find((period) => period.holdings.length > 0) ??
    account.periods[account.periods.length - 1]
  const { marketValue, costBasis, unrealized, unrealizedPercent } =
    snapshotTotals(latest.holdings)

  return {
    id: account.id,
    accountNumber: account.accountNumber,
    institution: account.institution,
    accountType: account.accountType,
    validated: account.validated,
    failingSections: account.failingSections,
    latestPeriodKey: latest.period,
    latestPeriodLabel: latest.label,
    marketValue,
    costBasis,
    unrealized,
    unrealizedPercent,
    cashBalance: latest.cashBalance,
    accountValue: accountValue(
      marketValue,
      latest.cashBalance,
      latest.holdings,
    ),
    realizedPrinted: account.realizedPrinted,
    holdingsCount: latest.holdings.filter(isPosition).length,
    periodCount: account.periods.length,
  }
}

// Each account's cash balance per period, so a snapshot other than the latest can total up too.
const cashSnapshots = (account: PortfolioAccount): CashSnapshot[] =>
  account.periods.map((period) => ({
    account: account.id,
    period: period.period,
    cashBalance: period.cashBalance,
  }))

// Flatten one account's periods into rows tagged with their account and period, so tables can
// read across accounts and periods at once.
const flattenAccount = (
  account: PortfolioAccount,
): {
  holdings: HoldingRow[]
  activity: ActivityRow[]
  realized: RealizedRow[]
  unvestedAwards: UnvestedAwardRow[]
  yearToDate: YearToDateRow[]
} => {
  const holdings: HoldingRow[] = []
  const activity: ActivityRow[] = []
  const realized: RealizedRow[] = []
  const unvestedAwards: UnvestedAwardRow[] = []
  const yearToDate: YearToDateRow[] = []

  for (const period of account.periods) {
    const context: RowContext = {
      account: account.id,
      accountLabel: account.institution,
      accountNumber: account.accountNumber,
      accountType: account.accountType,
      period: period.period,
      periodLabel: period.label,
    }

    for (const holding of period.holdings) {
      holdings.push({
        ...holding,
        ...context,
        gainLoss: snapshotTotals([holding]).unrealized,
      })
    }
    for (const entry of period.activity) {
      activity.push({ ...entry, ...context })
    }
    for (const gain of period.realizedGains) {
      realized.push({ ...gain, ...context })
    }
    for (const award of period.unvestedAwards) {
      unvestedAwards.push({ ...award, ...context })
    }
    for (const figure of period.yearToDate) {
      yearToDate.push({ ...figure, ...context })
    }
  }

  return { holdings, activity, realized, unvestedAwards, yearToDate }
}

// Distinct statement periods across every account, newest first. Keyed by period key so two
// accounts sharing a month collapse to one. A period with only activity still belongs in the list.
const distinctPeriods = (
  rows: { period: string; periodLabel: string }[],
): PortfolioPeriodOption[] => {
  const byKey = new Map<string, string>()
  for (const row of rows) {
    if (!byKey.has(row.period)) byKey.set(row.period, row.periodLabel)
  }
  return Array.from(byKey.entries())
    .map(([key, label]) => ({ key, label }))
    .sort((first, second) => second.key.localeCompare(first.key))
}

// Fold the project's securities into the shape the Portfolio dashboard renders: per-account
// summaries, distinct periods, and flattened holding/activity/realized rows. Read-only.
export const usePortfolioDashboard = (): PortfolioDashboard => {
  const accounts = useProjectSecurities()

  return useMemo(() => {
    const summaries = accounts.map(summarize)
    const holdings: HoldingRow[] = []
    const activity: ActivityRow[] = []
    const realized: RealizedRow[] = []
    const unvestedAwards: UnvestedAwardRow[] = []
    const yearToDate: YearToDateRow[] = []
    const cashBalances: CashSnapshot[] = []

    for (const account of accounts) {
      const flat = flattenAccount(account)
      holdings.push(...flat.holdings)
      activity.push(...flat.activity)
      realized.push(...flat.realized)
      unvestedAwards.push(...flat.unvestedAwards)
      yearToDate.push(...flat.yearToDate)
      cashBalances.push(...cashSnapshots(account))
    }

    return {
      accounts: summaries,
      periods: distinctPeriods([...holdings, ...activity, ...realized]),
      holdings,
      activity,
      realized,
      unvestedAwards,
      yearToDate,
      cashBalances,
    }
  }, [accounts])
}
